<script setup>
import { computed, reactive, ref, watch } from "vue";
import { GqlService } from "@js/services/gql";
import { formatCurrency } from "@js/utils/currency";
import StepperBar from "@js/vue/components/StepperBar.vue";
import AddressSearch from "@js/vue/components/AddressSearch.vue";
import ExpeditionTile from "@js/vue/components/ExpeditionTile.vue";

const props = defineProps({
	transportPolicyId: {
		type: Number,
		required: true,
	},
	transportDate: {
		type: String,
		default: () => new Date().toISOString().split("T")[0],
	},
	currencySymbol: {
		type: String,
		required: true,
	},
	itemValue: Number,
	packageName: String,
	reference: String,
	previousUrl: {
		type: String,
		required: true,
	},
});

// Form data
const formData = reactive({
	transportPolicyId: props.transportPolicyId,
	locationData: null,
	warehouseDate: null,
	storageDuration: null,
});

const periodOptions = {
	1: "1 month",
	2: "2 months",
	3: "3 months",
	4: "4 months",
	5: "5 months",
	6: "6 months",
	7: "7 months",
	8: "8 months",
	9: "9 months",
	10: "10 months",
	11: "11 months",
	12: "1 year",
	24: "2 years",
	36: "3 years",
	48: "4 years",
	60: "5 years",
};

// Stepper
const stepperRef = ref(null);
const steps = ["Storage details", "Confirm & Accept"];
const currentStep = ref(0);

watch(currentStep, (newStep) => {
	if (newStep === 1) {
		initializeStep1();
	}
});

// Form state
const isSubmitting = ref(false);
const errors = ref({});
const message = ref("");
const messageType = ref(""); // 'success' or 'error'

// To keep the code consistent, we still use steps here. But this form kinda doesn't need it
const validateStep = (stepIndex) => {
	stepIndex = stepIndex ?? currentStep.value;

	errors.value = getStepErrors(stepIndex);

	return Object.keys(errors.value).length === 0;
};

const isStepValid = (stepIndex) => {
	stepIndex = stepIndex ?? currentStep.value;

	return Object.keys(getStepErrors(stepIndex)).length === 0;
};

const getStepErrors = (stepIndex) => {
	stepIndex = stepIndex ?? currentStep.value;
	const newErrors = {};

	switch (stepIndex) {
		case 0:
			if (!formData.locationData) {
				newErrors.locationData = "Storage location is required";
			}

			if (!formData.warehouseDate) {
				newErrors.warehouseDate = "Warehouse date is required";
			}

			if (!formData.storageDuration) {
				newErrors.storageDuration = "Storage period is required";
			}

			return newErrors;

		default:
			return {};
	}
};

// Step 1
const summaryData = ref({});
const initializeStep1 = async () => {
	const json = await GqlService.request(
		`
			query StorageCalculation(
				$transportPolicyId: ID!,
				$storageDuration: Int!,
				$locationData: LocationData!,
			) {
				storageCalculation(
					transportPolicyId: $transportPolicyId,
					storageDuration: $storageDuration,
					locationData: $locationData,
				) {
					imaginaryProfit
					storageMonthlyPremium
					storagePremium
				}
			}
		`,
		formData,
	);

	summaryData.value = json.data.storageCalculation;
};
const warehouseDateFormatted = computed(() =>
	new Date(formData.warehouseDate).toLocaleDateString("en-GB"),
);

// Submit form
const handleSubmit = async () => {
	if (currentStep.value === 0) {
		stepperRef.value.nextStep();
		return;
	}

	// validation
	if (!validateStep(0)) return;

	isSubmitting.value = true;
	message.value = "";

	const submitted = await GqlService.request(
		`
			mutation AddStoragePolicy(
				$transportPolicyId: ID!,
				$storageDuration: Int!,
				$warehouseDate: DateTime!,
				$locationData: LocationData!,
			) {
				addStoragePolicy(
					transportPolicyId: $transportPolicyId,
					storageDuration: $storageDuration,
					warehouseDate: $warehouseDate,
					locationData: $locationData,
				) {
					id
				}
			}
		`,
		formData,
	);

	if (submitted.errors) {
		message.value = submitted.errors[0].message;
		messageType.value = "error";
		return;
	}

	if (!submitted.data.addStoragePolicy?.id) {
		message.value = "An error occurred while submitting the policy";
		messageType.value = "error";
		return;
	}

	window.location.href = props.previousUrl;
};
</script>

<template>
	<!-- Stepper -->
	<stepper-bar
		v-model:current-step="currentStep"
		:steps="steps"
		:is-step-valid="isStepValid"
		ref="stepperRef"
	/>

	<div class="row justify-content-center form-container">
		<div class="col-12 col-xl-9">
			<!-- Success/Error Messages -->
			<div
				v-if="message"
				:class="[
					'alert',
					messageType === 'success' ? 'alert-success' : 'alert-danger',
				]"
				role="alert"
			>
				{{ message }}
			</div>

			<!-- Form -->
			<form @submit.prevent="handleSubmit">
				<!-- Step 1 -->
				<div v-if="currentStep === 0" class="step-content row mb-4">
					<!-- Heading -->
					<h1 class="section-title text-dark-blue text-center font-medium">
						Add storage insurance
					</h1>

					<div class="section-heading text-center">
						<div class="section-text text-dark-blue">
							Please select the timeframe for which you want to extend your
							insurance.
						</div>
					</div>

					<!-- Storage location -->
					<div class="col-12 mb-3">
						<label class="form-label" for="storageLocation">
							Storage location <span class="required">*</span>
						</label>

						<address-search
							v-model:address-data="formData.locationData"
							:is-valid="!('storageLocation' in errors)"
							input-id="storageLocation"
						></address-search>

						<div v-if="errors.locationData" class="invalid-feedback">
							{{ errors.locationData }}
						</div>
					</div>

					<!-- Transport date -->
					<div class="col-12 mb-3">
						<label class="form-label" for="warehouseDate">
							Warehouse-in date <span class="required">*</span>
						</label>

						<input
							class="form-control"
							:class="{ 'is-invalid': errors.warehouseDate }"
							type="date"
							v-model="formData.warehouseDate"
							:min="transportDate"
							id="warehouseDate"
							required
						/>
						<div v-if="errors.warehouseDate" class="invalid-feedback">
							{{ errors.warehouseDate }}
						</div>
					</div>

					<div class="col-12 mb-3">
						<label class="form-label" for="storagePeriod">
							Storage period <span class="required">*</span>
						</label>

						<select
							class="form-select"
							id="storagePeriod"
							v-model="formData.storageDuration"
						>
							<option
								v-for="(label, value) in periodOptions"
								:key="value"
								:value="parseInt(value)"
							>
								{{ label }}
							</option>
						</select>
					</div>
				</div>

				<!-- Step 2 -->
				<div v-if="currentStep === 1" class="step-content row mb-4">
					<!-- Heading -->
					<h1 class="section-title text-dark-blue text-center font-medium">
						Almost done - complete your insurance
					</h1>

					<div class="section-heading text-center">
						<div class="section-text text-dark-blue">
							Please find the summary of your insurance package below. Make sure
							to double check all information! Is everything in order? Click '<b
								>Confirm & Accept</b
							>' to complete your insurance for the storage.
						</div>
					</div>

					<div class="col-12 mb-3">
						<h3>Expedition overview:</h3>
						<expedition-tile
							:shipmentType="{ label: 'Storage', icon: 'ri-archive-fill' }"
							:storageAddress="formData.locationData.fullAddress"
							:storage-period="periodOptions[formData.storageDuration]"
							:reference="reference ?? '-'"
							:warehouseDate="warehouseDateFormatted"
						></expedition-tile>
					</div>

					<div class="col-12 mb-3" v-if="Object.keys(summaryData).length > 0">
						<div class="financial-overview mb-3">
							<h5 class="mb-3">Insurance overview:</h5>
							<div class="financial-group">
								<div class="row align-items-center g-0">
									<div class="col item item-title">
										<strong>Selected insurance package</strong>
									</div>
									<div class="item item-price col-auto text-end">
										{{ packageName }}
									</div>
								</div>
							</div>
							<div class="financial-group">
								<div class="row align-items-center g-0">
									<div class="col item item-title">
										<strong>Value of goods</strong>
									</div>
									<div class="item item-price col-auto text-end">
										{{ formatCurrency(itemValue, currencySymbol) }}
									</div>
								</div>
							</div>
							<div class="financial-group">
								<div class="row align-items-center g-0">
									<div class="col item item-title">
										<strong>Imaginary profit</strong>
									</div>
									<div class="item item-price col-auto text-end">
										{{
											formatCurrency(
												summaryData.imaginaryProfit,
												currencySymbol,
											)
										}}
									</div>
								</div>
							</div>

							<div class="financial-group total">
								<div class="row align-items-center g-0 text-blue font-bold">
									<div class="col item item-title">Total</div>
									<div class="item item-price col-auto text-end">
										{{
											formatCurrency(
												itemValue + summaryData.imaginaryProfit,
												currencySymbol,
											)
										}}
									</div>
								</div>
							</div>
						</div>
						<h5 class="float-start">
							Your insurance premium - '{{ packageName }}'
						</h5>
						<div class="float-end">
							<h5>
								{{ formatCurrency(summaryData.storagePremium, currencySymbol) }}
							</h5>
							{{
								formatCurrency(
									summaryData.storageMonthlyPremium,
									currencySymbol,
								)
							}}
							per month
						</div>
					</div>
					<div v-else class="spinner-border" role="status">
						<span class="visually-hidden"></span>
					</div>
				</div>

				<!-- Action Buttons -->
				<div class="col-12 mb-3">
					<div class="row justify-content-between">
						<div class="col-auto">
							<a
								:href="previousUrl"
								class="btn btn-rounded btn-outline-blue"
								v-if="previousUrl && currentStep === 0"
							>
								<i class="ri-arrow-left-line" aria-hidden="true"></i>
								<span>Back</span>
							</a>
							<button
								class="btn btn-rounded btn-outline-blue"
								v-else-if="currentStep > 0"
								@click.prevent="stepperRef.prevStep()"
							>
								<i class="ri-arrow-left-line" aria-hidden="true"></i>
								<span>Back</span>
							</button>
						</div>
						<div class="col-auto">
							<button
								class="btn btn-rounded btn-blue"
								type="submit"
								:disabled="!isStepValid() || isSubmitting"
							>
								<span v-if="isSubmitting">Adding...</span>
								<span v-else-if="currentStep === 1">Confirm & Accept</span>
								<span v-else>Next</span>
								<i class="ri-arrow-right-line" aria-hidden="true"></i>
							</button>
						</div>
					</div>
				</div>
			</form>
		</div>
	</div>
</template>

<style scoped>
.spinner-border {
	--bs-spinner-width: 100px;
	--bs-spinner-height: 100px;
	margin: 0 auto;
}
</style>
